<script setup>
import MyBet from './Partials/MyBet.vue';
import TextField from '@/components/Form/TextField.vue';
import SelectField from '@/components/Form/SelectField.vue';
import DefaultButton from '@/components/Buttons/DefaultButton.vue';
import { useAuthStore } from '@/stores/auth';
import { useBetsStore } from '@/stores/bets';
import { storeToRefs } from 'pinia';
import { onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { modelDateValue, formatToLocalDate, toFixedTwo } from '@/helpers';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const bets = useBetsStore();
const auth = useAuthStore();
const route = useRoute();

const { getUser, getCurrencies, getCurrency } = storeToRefs(auth);
const { getTickets, getUsers: ticketUsers } = storeToRefs(bets);

const activeTab = ref(0);
const tabs = ref({
  0: t('tabs.0'),
  1: t('tabs.1'),
  2: t('tabs.2'),
  3: t('tabs.3'),
});

const tempFilters = ref({
  userId: getUser.value._id,
  startDate: modelDateValue(),
  endDate: modelDateValue(),
  currency: '',
});

const filters = ref({
  userId: getUser.value._id,
  startDate: modelDateValue(),
  endDate: modelDateValue(),
  currency: '',
});

const applyFilters = () => {
  filters.value = { ...tempFilters.value };
  fetch();
};

const setActiveTab = (tab) => {
  activeTab.value = tab;

  if (tab == 1) {
    filters.value.st = 6; 
  } else if (tab == 2) {
    filters.value.st = 1; 
  } else if (tab == 3) {
    filters.value.st = 2; 
  } else {
    delete filters.value.st;
  }

  fetch();
};

const fetch = () => {
  bets.fetchTickets(filters.value);
};

watch(() => route.query, (value) => {
  if (value.uid) {
    filters.value.userId = value.uid;
  } else {
    filters.value.userId = getUser.value._id;
  }
});

watch(filters, () => {
    fetch()
}, {deep: true})

onMounted(() => {
  tempFilters.value.currency = getCurrencies.value[0]._id;
  filters.value.currency = getCurrencies.value[0]._id;
  fetch(); 
});
</script>

<template>
  <div>
    <form @submit.prevent>
      <div class="px-4 py-2 flex flex-col md:flex-row md:gap-2">
        <TextField
          type="date"
          v-model="tempFilters.startDate"
          placeholder="Start Date"
          class="w-full rounded-b-none"
        />
        <TextField
          type="date"
          v-model="tempFilters.endDate"
          placeholder="End Date"
          class="w-full border-t border-neutral-200 rounded-none"
        />
        <SelectField
          v-model="tempFilters.currency"
          :options="getCurrencies"
          text-attribute="cod"
          value-attribute="_id"
          class="w-full rounded-t-none border-t border-neutral-200"
        />
        <DefaultButton
          @click.prevent="applyFilters"
          class="w-full md:w-auto"
        >
          {{ $t('general.26') }}
        </DefaultButton>
      </div>
    </form>

    <template v-if="!ticketUsers.length">
      <div class="h-12 px-4 flex">
        <ul class="list-none flex items-center space-x-2">
          <li
            v-for="tab, i in tabs"
            :key="i"
            :class="['tab-item', { active: activeTab == i }]"
            @click="setActiveTab(i)"
          >
            <span>{{ tab }}</span>
          </li>
        </ul>
      </div>

      <div class="px-4 py-2 space-y-4">
        <template v-if="getTickets.length">
          <div v-for="group in getTickets" :key="group._id">
            <h3 class="text-sm font-bold text-neutral-400 mb-2">
              {{ formatToLocalDate(group.tickets[0].date) }}
            </h3>
            <ul class="space-y-2">
              <li v-for="ticket in group.tickets" :key="ticket._id">
                <MyBet
                  :ticket="ticket"
                  @cashout="fetch"
                />
              </li>
            </ul>
          </div>
        </template>

        <div class="no-Tickets" v-else>
          <p class="text-sm font-bold text-neutral-300 text-center">
            {{ $t('app.11') }}
          </p>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="px-4 py-2">
        <ul class="list-none space-y-2">
          <li
            v-for="user in ticketUsers"
            :key="user._id"
            class="text-neutral-300"
          >
            <RouterLink
              :to="`/my-bets?uid=${user._id}`"
              class="flex justify-between no-Tickets px-4 cursor-pointer"
            >
              <p class="text-sm font-bold">
                <span>{{ user.un }}</span>
                <span class="ml-1 text-xs font-normal">({{ user.tnr }})</span>
              </p>
              <p class="text-xs">
                {{ toFixedTwo(user.am) }} {{ getCurrency(filters.currency)?.cod }}
              </p>
            </RouterLink>
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<style>
.tab-item {
  @apply inline-flex items-center px-2 py-1 rounded-2xl cursor-pointer;
}

.tab-item span {
  @apply text-white text-sm;
}

.tab-item.active {
  background-color: var(--color-primary3);
}

.tab-item.active span {
  color: #000;
  font-weight: 700;
}
.no-Tickets {
  background-image: var(--mgc-bg);
  @apply rounded-md overflow-hidden py-4;
}
</style>
